import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Link,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { Help as HelpIcon, Error as ErrorIcon } from '@material-ui/icons';
import MultiSelector from '../../integration-wizard/components/common/MultiSelector';
import InfoBlock from '../../integration-wizard/components/common/InfoBlock';
import styles from './IntegrationSettings.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const VIEW_SELECTOR_FIELDS = {
  searchBox: {
    desktop: 'selectors',
    mobile: 'mobileSelectors',
    titleKey: 'ui.searchSettings.searchBoxIntegrationSettings.searchBoxTriggerSelectors',
    surfaceNameKey: 'search.settings.surface.searchBox',
  },
  dropdown: {
    desktop: 'inputSelectors',
    mobile: 'mobileInputSelectors',
    titleKey: 'ui.searchSettings.searchBoxIntegrationSettings.searchDropdownTriggerSelectors',
    surfaceNameKey: 'search.settings.surface.searchDropdown',
  },
};

const SearchBoxIntegrationSettings = ({
  view,
  title,
  config,
  saveConfig: saveFn,
  updateViewIntegration,
  onReady,
  onRegisterSave,
  onUpdate,
}) => {
  const t = useLocalizedMessage();
  const [localSelectors, setLocalSelectors] = useState({ 0: [], 1: [] });
  const [deviceTab, setDeviceTab] = useState(0);
  const fields = VIEW_SELECTOR_FIELDS[view] || VIEW_SELECTOR_FIELDS.searchBox;

  useEffect(() => {
    if (!config) return;
    const integration = config?.search?.views?.[view]?.integration;
    setLocalSelectors({
      0: integration?.[fields.desktop] || [],
      1: integration?.[fields.mobile] || [],
    });
  }, [config, fields.desktop, fields.mobile, view]);

  const saveConfig = useCallback(async () => {
    if (!config || !updateViewIntegration || !saveFn) return;

    const integration = {
      ...(config?.search?.views?.[view]?.integration || {}),
      [fields.desktop]: localSelectors[0].filter((selector) => selector.trim()),
      [fields.mobile]: localSelectors[1].filter((selector) => selector.trim()),
    };

    const updatedConfig = updateViewIntegration(view, integration);
    if (updatedConfig) {
      await saveFn(updatedConfig);
    }
  }, [config, fields.desktop, fields.mobile, localSelectors, saveFn, updateViewIntegration, view]);

  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(saveConfig);
    }
  }, [onRegisterSave, saveConfig]);

  const handleSelectorsUpdate = (newValues) => {
    setLocalSelectors((prev) => ({ ...prev, [deviceTab]: newValues }));
    if (onUpdate) onUpdate();
  };

  useEffect(() => {
    if (onReady) onReady(true);
  }, [onReady]);

  if (!config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  const hasDesktopSelector = localSelectors[0]?.some((selector) => selector.trim());

  return (
    <Box className={styles.root}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={7}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <Typography component="h2" className={styles.title}>
                {title || t('search.settings.searchBox.integrationTitle')}
              </Typography>
            </div>

            <Typography component="h3" className={styles.sectionTitle}>
              {t(fields.titleKey)}
            </Typography>
            <InfoBlock
              title={<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.whatIsThis" />}
              description={t('search.settings.searchBox.selectorDescription', { surface: t(fields.surfaceNameKey) })}
              items={[
                <>
                  <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.addSelectorsForYourExistingSearchIconOrButtonFor" />
                  {' '}
                  <code>.header-search-icon</code>
                  ).
                </>,
                <>
                  <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.addSelectorsForExistingSearchInputFieldsForExample" />
                  {' '}
                  <code>input[name=&quot;q&quot;]</code>
                  ).
                </>,
              ]}
            />

            <Tabs
              className={styles.deviceTabs}
              value={deviceTab}
              onChange={(e, value) => setDeviceTab(value)}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              <Tab className={styles.deviceTab} label={<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.desktop" />} />
              <Tab className={styles.deviceTab} label={<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.mobile" />} />
            </Tabs>

            <MultiSelector
              label={t(deviceTab === 0 ? 'search.settings.selectors.desktop' : 'search.settings.selectors.mobile')}
              selectors={localSelectors[deviceTab] || ['']}
              onChange={handleSelectorsUpdate}
              helperText={<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.addMultipleSelectorsWhenSearchCanOpenFromMoreThan" />}
            />

            {!hasDesktopSelector && deviceTab === 0 && (
              <div className={styles.warning}>
                <ErrorIcon />
                <Typography variant="body2">
                  <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.noDesktopSelectorIsConfiguredThe" />
                  {' '}
                  {t(fields.surfaceNameKey)}
                  {' '}
                  <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.willNotAppearOnYourStorefront" />
                </Typography>
              </div>
            )}
          </section>
        </Grid>

        <Grid item xs={12} md={5}>
          <aside className={styles.helpPanel}>
            <div className={styles.helpHeader}>
              <span className={styles.helpIcon}><HelpIcon /></span>
              <Typography component="h2" className={styles.helpTitle}>
                <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.findingCssSelectors" />
              </Typography>
            </div>

            <Typography variant="body1" paragraph>
              <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.aCssSelectorIdentifiesTheStorefrontElementThatShouldOpen" />
            </Typography>

            <ul className={styles.helpList}>
              <li><Typography><IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.rightClickTheElementAndChoose" />{' '}<b><IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.inspect" /></b>.</Typography></li>
              <li><Typography><IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.lookForAStable" />{' '}<code>id</code>{' '}<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.or" />{' '}<code>class</code>{' '}<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.attribute" /></Typography></li>
              <li><Typography><IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.use" />{' '}<code>#header-search</code>{' '}<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.forAnId" /></Typography></li>
              <li><Typography><IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.use" />{' '}<code>.search-input</code>{' '}<IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.forAClass" /></Typography></li>
            </ul>

            <div className={styles.reference}>
              <Typography variant="subtitle2" className={styles.referenceTitle}>
                <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.selectorReference" />
              </Typography>
              <Link
                className={styles.referenceLink}
                href="https://www.w3schools.com/CSS/css_selectors.asp"
                target="_blank"
                rel="noopener"
              >
                <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.openTheCssSelectorsGuide" />
              </Link>
              <Typography variant="caption" display="block" color="textSecondary">
                <IntlMessages id="ui.searchSettings.searchBoxIntegrationSettings.contactSupportIfTheThemeMarkupChangesBetweenDesktopAnd" />
              </Typography>
            </div>
          </aside>
        </Grid>
      </Grid>
    </Box>
  );
};

SearchBoxIntegrationSettings.propTypes = {
  view: PropTypes.oneOf(['searchBox', 'dropdown']),
  title: PropTypes.string,
  config: PropTypes.shape({}),
  saveConfig: PropTypes.func,
  updateViewIntegration: PropTypes.func,
  onReady: PropTypes.func,
  onRegisterSave: PropTypes.func,
  onUpdate: PropTypes.func,
};

SearchBoxIntegrationSettings.defaultProps = {
  view: 'searchBox',
  title: null,
  config: null,
  saveConfig: null,
  updateViewIntegration: null,
  onReady: null,
  onRegisterSave: null,
  onUpdate: null,
};

export default SearchBoxIntegrationSettings;
